import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Suspense } from "react";
import {
	Tabs,
	TabsIndicator,
	TabsList,
	TabsPanel,
	TabsTab,
} from "@/components/Tabs";
import { orpc } from "@/orpc/client";
import { AppSettings } from "./app-settings/AppSettings";
import { AppSettingsSkeleton } from "./app-settings/AppSettingsSkeleton";
import { Alerts } from "./components/Alerts";
import { AppPartnersDialog } from "./components/AppPartnersDialog";
import { AppPartnersDialogSkeleton } from "./components/AppPartnersDialogSkeleton";
import { ReferralLinks } from "./referral-links/ReferralLinks";
import { ReferralLinksSkeleton } from "./referral-links/ReferralLinksSkeleton";

const VALID_TABS = ["referral_links", "settings"] as const;
type TabValue = (typeof VALID_TABS)[number];

export function AppDetail() {
	const { appId } = useParams({ from: "/_app/apps/$appId/" });
	const navigate = useNavigate();
	const search = useSearch({ from: "/_app/apps/$appId/" });

	const { data: app } = useSuspenseQuery(
		orpc.apps.detail.queryOptions({ input: { id: appId } }),
	);

	const tab = search.tab;
	const isPartnersDialogOpen = !!search.partners;

	// Ensure tab is valid, default to referral_links
	const currentTab = VALID_TABS.includes(tab as TabValue)
		? tab
		: "referral_links";

	const handleTabChange = (value: string | number | null) => {
		if (typeof value === "string" && VALID_TABS.includes(value as TabValue)) {
			navigate({
				to: "/apps/$appId",
				params: { appId },
				search: (prev) => ({ ...prev, tab: value as TabValue }),
			});
		}
	};

	const setTab = (value: string) => {
		navigate({
			to: "/apps/$appId",
			params: { appId },
			search: (prev) => ({ ...prev, tab: value as TabValue }),
		});
	};

	const setIsPartnersDialogOpen = (value: boolean) => {
		navigate({
			to: "/apps/$appId",
			params: { appId },
			search: (prev) => ({
				...prev,
				tab: prev.tab as TabValue,
				partners: value ? true : undefined,
			}),
		});
	};

	return (
		<>
			<Alerts app={app} setTab={setTab} />

			<Tabs
				value={currentTab}
				onValueChange={handleTabChange}
				className="w-full"
			>
				<TabsList>
					<TabsTab value="referral_links">Referral Links</TabsTab>
					<TabsTab value="settings">Settings</TabsTab>
					<TabsIndicator />
				</TabsList>
				<TabsPanel value="referral_links">
					<div className="py-4">
						<Suspense fallback={<ReferralLinksSkeleton />}>
							<ReferralLinks />
						</Suspense>
					</div>
				</TabsPanel>
				<TabsPanel value="settings">
					<div className="py-4">
						<Suspense fallback={<AppSettingsSkeleton />}>
							<AppSettings
								onOpenPartnersDialog={() => setIsPartnersDialogOpen(true)}
							/>
						</Suspense>
					</div>
				</TabsPanel>
			</Tabs>

			{isPartnersDialogOpen && (
				<Suspense
					fallback={
						<AppPartnersDialogSkeleton
							isOpen={true}
							onClose={() => setIsPartnersDialogOpen(false)}
						/>
					}
				>
					<AppPartnersDialog
						appId={appId}
						isOpen={isPartnersDialogOpen}
						onClose={() => setIsPartnersDialogOpen(false)}
					/>
				</Suspense>
			)}
		</>
	);
}
